<template>
  <v-dialog
    :value="dialog"
    :max-width="400"
    persistent
    :retain-focus="false"
    @keydown.esc="escape(closeDialog)"
    @click:outside="escape(closeDialog)"
    @keydown.enter="submit"
  >
    <v-card outlined>
      <v-card-title>
        {{ dialogTitle }}
        <p-btn-icon
          type="close"
          btn-class="mr-n3 ml-auto"
          tabindex="4"
          @click="escape(closeDialog)"
        ></p-btn-icon>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-form ref="form" autocomplete="off" @submit.stop.prevent>
          <v-container>
            <v-row dense align="center" justify="end">
              <v-col>
                <v-select
                  v-model="editedBudget.budgetCategoryId"
                  :items="budgetCategories"
                  :rules="rules.budgetCategoryId"
                  validate-on-blur
                  item-value="id"
                  item-text="name"
                  tabindex="1"
                  autofocus
                >
                  <template v-slot:append-item>
                    <v-divider></v-divider>
                    <div class="p-list-add-item-link" @click="openEditDialog">
                      {{ $t('budget.addNewCategory') }}
                    </div>
                  </template>
                </v-select>
                <v-text-field
                  type="text"
                  v-model.trim="editedBudget.name"
                  :label="$t('budget.edit.name')"
                  :rules="rules.name"
                  tabindex="1"
                  validate-on-blur
                  clearable
                ></v-text-field>
                <p-number-input
                  v-model="editedBudget.quantity"
                  :label="$t('budget.edit.quantity')"
                  :rules="rules.quantity"
                  tabindex="1"
                  validate-on-blur
                  integer
                  clearable
                ></p-number-input>
                <p-number-input
                  v-model="editedBudget.amount"
                  :label="$t('budget.edit.amount')"
                  :rules="rule.amount"
                  tabindex="1"
                  validate-on-blur
                  clearable
                ></p-number-input>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="justify-center">
        <v-btn text tabindex="3" @click="closeDialog">{{ $t('cancel') }}</v-btn>
        <v-btn text :disabled="!isModelChanged()" color="primary" tabindex="2" @click="submit">{{ $t('save') }}</v-btn>
      </v-card-actions>
    </v-card>
    <budget-category-edit-dialog
      :dialog.sync="editBudgetCategoryDialog"
      :budgetCategory="null"
      :income="income"
      :incomeDisabled="true"
      @add="budgetCategoryAdded"
    ></budget-category-edit-dialog>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import BudgetCategoryEditDialog from '@/views/Settings/BudgetCategories/BudgetCategoryEditDialog'
import PNumberInput from '@/components/PNumberInput'
import PBtnIcon from '@/components/PBtnIcon'

import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'


export default {
  components: {
    BudgetCategoryEditDialog,
    PNumberInput,
    PBtnIcon
  },


  mixins: [
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin
  ],


  props: {
    budget: Object,
    income: Boolean
  },


  data () {
    return {
      editedBudget: {},
      defaultBudget: {
        name: '',
        quantity: 1,
        amount: null,
        budgetCategoryId: null
      },
      editBudgetCategoryDialog: false
    }
  },


  computed: {
    ...mapGetters('project', ['currentProjectId']),
    ...mapGetters('budgetCategory', ['budgetCategoriesByType']),

    dialogTitle () {
      if (this.budget) {
        return this.income === true ? this.$t('budget.editIncome') : this.$t('budget.editExpense')
      } else {
        return this.income === true ? this.$t('budget.addIncome') : this.$t('budget.addExpense')
      }
    },

    budgetCategories () {
      return this.budgetCategoriesByType(this.income)
    },

    rulesToApply () {
      const rule = this.rule

      return {
        budgetCategoryId: [rule.required()],
        name: [rule.required()],
        quantity: [rule.required()],
        account: [rule.required()]
      }
    }
  },


  methods: {
    ...mapActions('budget', ['createBudget', 'updateBudget']),

    onDialogOpen () {
      this.editedBudget = this.getObjectCopy(this.budget || this.defaultBudget)

      if (!this.editedBudget.budgetCategoryId && this.budgetCategories.length > 0) {
        this.editedBudget.budgetCategoryId = this.budgetCategories[0].id
      }

      this.initWatchedModel(this.editedBudget)
      this.resetValidation()
    },

    openEditDialog () {
      this.editBudgetCategoryDialog = true
    },

    budgetCategoryAdded (budgetCategory) {
      this.editedBudget.budgetCategoryId = budgetCategory.id
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return

      try {
        this.showSnackbarProcessing()
        const budget = this.getObjectCopy(this.editedBudget)
        budget.quantity = parseInt(budget.quantity)
        budget.amount = parseInt(budget.amount)

        if (budget.id) {
          await this.updateBudget(budget)
          this.showSnackbarSuccess(this.$t('budget.budgetUpdated'))
        } else {
          budget.projectId = this.currentProjectId
          await this.createBudget(budget)
          this.showSnackbarSuccess(this.$t('budget.budgetCreated'))
        }

        this.$emit('saved')
        this.closeDialog()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  }
}
</script>
